import { useEffect, useState } from "react";

import { ApiRequester } from "../helpers/ApiRequest";
import { _ } from "../../../vendor";
import {
  ChartDataMapper,
  ChartDataObjMapper,
  filterGeneratorPieChart,
  TranslateAndReturnChartDataObjMapper,
} from "../../utils/ChartDataHelperOld";
import t from "./t";

/**
 * All hooks return an array of three elements.
 * - value
 * - isComplete
 * - query
 */

const defChartData = { json: [] };

// Total number of bookings per month
const filterTotalBookingPerMonth = data => {
  const valSum = obj => Object.values(obj).reduce((a, b) => a + b);
  const prop = "bookingsPerBookingType";
  const elemKey = t("total_bookings");
  const newData = data.map(x => ({
    [elemKey]: _.isObject(x[prop]) ? valSum(x[prop]) : 0,
    date: x.date,
  }));
  return ChartDataMapper(elemKey)(newData);
};
// ... additional filter per usage
const filterBookingPerUsageTypePerMonth = ChartDataObjMapper(
  "bookingsPerBookingType"
);
// ... additional filter per membership (user email domain)
const filterBookingsPerMembershipPerMonth = ChartDataObjMapper(
  "bookingsPerMembership"
);
// Total km per month
const filterTotalDistancePerMonth = ChartDataMapper("completeDistance");
// Total number of Bookings per usage type
const filterBookingsPerUsageType = filterGeneratorPieChart(
  "bookingsPerBookingType"
);
// Total number of Distance per usage type
const filterDistancesPerUsageType = filterGeneratorPieChart(
  "distancesPerBookingType"
);
const filterUsersPerMembership = filterGeneratorPieChart();
const filterBookingsPerMembership = filterGeneratorPieChart();

const filterCo2SavingsToCarInKG = filterGeneratorPieChart(
  "co2SavingsToCarInKG"
);

export const useSummary = () => {
  const [query, setQuery] = useState();
  const [isCompleted, setCompleted] = useState(false);
  const [totalBooking, setTotalBooking] = useState(null);
  const [totalDistance, setTotalDistance] = useState(null);
  const [distancePerBike, setDistancePerBike] = useState(null);
  const [distancePerBooking, setDistancePerBooking] = useState(null);
  const [bookingsPerUsageType, setBookingsPerUsageType] =
    useState(defChartData);
  const [distancesPerUsageType, setDistancesPerUsageType] =
    useState(defChartData);
  const [co2SavingsToCarInKG, setCo2SavingsToCarInKG] =
    useState(defChartData);

  useEffect(() => {
    if (query) {
      const fetchDataSummaryPerMonth = async () => {
        const response = await ApiRequester("/charts/getSummary")();
        const data = response.data;

        setBookingsPerUsageType(filterBookingsPerUsageType(data));
        setDistancesPerUsageType(filterDistancesPerUsageType(data));
        setCo2SavingsToCarInKG(filterCo2SavingsToCarInKG(data));

        const {
          completeOrderCount,
          completeDistance,
          distancePerBooking,
          distancePerBike,
        } = data;

        setTotalBooking(completeOrderCount);
        setTotalDistance(completeDistance);
        setDistancePerBooking(distancePerBooking);
        setDistancePerBike(distancePerBike);
        setCompleted(true);
      };
      setCompleted(false);
      fetchDataSummaryPerMonth(query);
    }
  }, [query]);

  return [
    {
      totalBooking,
      totalDistance,
      distancePerBooking,
      distancePerBike,
      bookingsPerUsageType,
      distancesPerUsageType,
      co2SavingsToCarInKG
    },
    isCompleted,
    setQuery,
  ];
};

export const useSummaryForAPeriod = () => {
  const [query, setQuery] = useState();
  const [isCompleted, setCompleted] = useState(false);
  const [totalBooking, setTotalBooking] = useState(null);
  const [totalDistance, setTotalDistance] = useState(null);
  const [distancePerBike, setDistancePerBike] = useState(null);
  const [distancePerBooking, setDistancePerBooking] = useState(null);
  const [co2SavingsFromNorderstedtToLuebeck, setCo2SavingsToNorderstedtLuebeck] = useState(null);
  const [co2SavingsToNorderstedt, setCo2SavingsToShortDrivesNorderstedt] = useState(null);
  const [bookingsPerUsageType, setBookingsPerUsageType] =
    useState(defChartData);
  const [distancesPerUsageType, setDistancesPerUsageType] =
    useState(defChartData);
  const [co2SavingsToCarInKG, setCo2SavingsToCarInKG] =
    useState(defChartData);

  useEffect(() => {
    if (query) {
      const fetchDataSummaryPerMonth = async (filter) => {
        /**
         * @type {{from: Date, to: Date}}
         */
        const { from, to } = filter;
        const payload = { from, to };
        const response = await ApiRequester("/charts/getSummaryForAPeriod", payload, "POST")();
        const data = response.data;

        setBookingsPerUsageType(filterBookingsPerUsageType(data));
        setDistancesPerUsageType(filterDistancesPerUsageType(data));
        setCo2SavingsToCarInKG(filterCo2SavingsToCarInKG(data));

        const {
          completeOrderCount,
          completeDistance,
          distancePerBooking,
          distancePerBike,
          co2SavingsToNorderstedtLuebeck,
          co2SavingsToShortDrivesNorderstedt
        } = data;

        setTotalBooking(completeOrderCount);
        setTotalDistance(completeDistance);
        setDistancePerBooking(distancePerBooking);
        setDistancePerBike(distancePerBike);
        setCo2SavingsToNorderstedtLuebeck(co2SavingsToNorderstedtLuebeck);
        setCo2SavingsToShortDrivesNorderstedt(co2SavingsToShortDrivesNorderstedt);
        setCompleted(true);
      };
      setCompleted(false);
      fetchDataSummaryPerMonth(query);
    }
  }, [query]);

  return [
    {
      totalBooking,
      totalDistance,
      distancePerBooking,
      distancePerBike,
      bookingsPerUsageType,
      distancesPerUsageType,
      co2SavingsToCarInKG,
      co2SavingsFromNorderstedtToLuebeck,
      co2SavingsToNorderstedt
    },
    isCompleted,
    setQuery,
  ];
};

export const useSummaryPerMonth = () => {
  const [query, setQuery] = useState();
  const [isCompleted, setCompleted] = useState(false);
  const [totalBooking, setTotalBooking] = useState(defChartData);
  const [bookingsPerUsageType, setBookingsPerUsageType] =
    useState(defChartData);
  const [bookingsPerMembership, setBookingsPerMembership] =
    useState(defChartData);
  const [totalDistance, setTotalDistance] = useState(defChartData);

  useEffect(() => {
    if (query) {
      const fetchDataSummaryPerMonth = async filter => {
        /**
         * @type {{from: Date, to: Date}}
         */
        const { from, to } = filter;
        const fy = from.getFullYear();
        const ty = to.getFullYear();
        const allyears = _.range(fy, ty + 1).map(year =>
          ApiRequester(`/charts/getSummaryPerMonthYear/${year}`)()
        );
        const response = await Promise.allSettled(allyears);
        const data = response.map(x => x.value.data).flat();

        setTotalBooking(filterTotalBookingPerMonth(data));
        setBookingsPerUsageType(filterBookingPerUsageTypePerMonth(data));
        setBookingsPerMembership(filterBookingsPerMembershipPerMonth(data));
        setTotalDistance(filterTotalDistancePerMonth(data));
        setCompleted(true);
      };
      setCompleted(false);
      fetchDataSummaryPerMonth(query);
    }
  }, [query]);

  return [
    {
      totalBooking,
      bookingsPerUsageType,
      bookingsPerMembership,
      totalDistance,
    },
    isCompleted,
    setQuery,
  ];
};

export const useCountOfUsersPerMembership = () => {
  const [query, setQuery] = useState();
  const [isCompleted, setCompleted] = useState(false);
  const [usersPerMembership, setUsersPerMembership] = useState(defChartData);

  useEffect(() => {
    if (query) {
      const fetchDataCountOfUsersPerMembership = async () => {
        const response = await ApiRequester(
          "/charts/getCountOfUsersPerMembership"
        )();
        const data = response.data;

        setUsersPerMembership(filterUsersPerMembership(data));
        setCompleted(true);
      };
      setCompleted(false);
      fetchDataCountOfUsersPerMembership(query);
    }
  }, [query]);
  return [usersPerMembership, isCompleted, setQuery];
};

export const useCountOfBookingsPerMembership = () => {
  const [query, setQuery] = useState();
  const [isCompleted, setCompleted] = useState(false);
  const [bookingsPerMembership, setBookingsPerMembership] =
    useState(defChartData);

  useEffect(() => {
    if (query) {
      const fetchDataCountOfBookingsPerMembership = async () => {
        const response = await ApiRequester(
          "/charts/getCountOfBookingsPerMembership"
        )();
        const data = response.data;

        setBookingsPerMembership(filterBookingsPerMembership(data));
        setCompleted(true);
      };
      setCompleted(false);
      fetchDataCountOfBookingsPerMembership(query);
    }
  }, [query]);

  return [bookingsPerMembership, isCompleted, setQuery];
};
